import { useFilter } from "hooks/useFilter";
import { IFilterAvaliacaoQuestionario } from "interfaces";
import { Main } from "pages/_layouts/Main";
import React, { FC, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { Filter } from "./components/Filter";
import { Filter as FilterIcon } from "mdi-material-ui";
import { format, set } from "date-fns";
import {
  IAvaQuestionario as IDMMAvaQuestionario,
  IMatricula,
  IQuestAvaliacao,
  IQuestionario,
  ITurma,
  utils,
  yupQuestAvaliacao,
} from "@deltasge/marauders-map";
import { api, Environment, history } from "configs";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { Float } from "components/Buttons/Float";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MUITableRow,
} from "@mui/material";
import { Pagination } from "components/Pagination";
import { IAvaQuestionario, TableRow } from "./components/TableRow";
import { useDialog } from "hooks/useDialog";
import { CadastroAvaQuestionario } from "./Cadastro/CadastroAvaQuestionario";
import { EmptyState } from "components/EmptyStates";
import { useConfirmDialog } from "hooks/useDialogConfirm";
import { useMobile } from "hooks/useMobile";
import { request as getPapeis } from "store/modules/relatorios/configuracoes/papeis";

export const AvaliacaoQuestionario: FC = () => {
  const rowsPerPageOptions = [25, 50, 100];

  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [loadingMatricula, setLoadingMatricula] = useState(false);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState(false);
  const [matriculas, setMatriculas] = useState<IMatricula[]>([]);
  const [avaliacoes, setAvaliacoes] = useState<[IDMMAvaQuestionario[], number]>(
    [[], 0]
  );
  const [avaliacao, setAvaliacao] = useState<Partial<IQuestAvaliacao>>();
  const [questionarios, setQuestionarios] = useState<IQuestionario[]>([]);

  const isXl = useMobile("xl");
  const isMd = useMobile("md");

  const dispatch = useAppDispatch();

  const {
    escola,
    treeView: { professores: profs },
    usuario: { usuario },
    tamanhoPapeis: { data: tamanhoPapeis },
    configPapelPadrao,
  } = useAppSelector((state) => state);

  const {
    RenderDialog: DialogCadastro,
    show: showCadastro,
    hide: handleHide,
  } = useDialog({
    title: avaliacao?.id ? "Editar avaliação" : "Cadastro de avaliação",
    options: {
      maxWidth: "md",
    },
  });

  const { RenderDialog: DialogDelete, show: handleDelete } =
    useConfirmDialog<IDMMAvaQuestionario>({
      defaults: {
        title: "Remover avaliação",
        content: "Deseja realmente remover esta avaliação e suas respostas?",
      },
      onConfirmed: async (payload, hide) => {
        try {
          await api.delete(`avaliacao-questionario/${payload.id}`);
          handleFilter();
        } catch (error) {
          snack.error(getError(error));
        } finally {
          hide();
        }
      },
    });

  const handleFilterAnoLetivo = (anoLetivo?: number) =>
    navigate({
      ...filter,
      anoLetivo,
      idCurso: undefined,
      idSerie: undefined,
      idTurma: undefined,
      idMateria: undefined,
    });

  const handleFilterCurso = (idCurso?: number) =>
    navigate({
      ...filter,
      idCurso,
      idSerie: undefined,
      idTurma: undefined,
      idMateria: undefined,
    });

  const handleFilterSerie = (idSerie?: number) =>
    navigate({
      ...filter,
      idSerie,
      idTurma: undefined,
      idMateria: undefined,
    });

  const handleFilterTurma = (idTurma?: number) =>
    navigate({
      ...filter,
      idTurma,
      idMateria: undefined,
    });

  const handleFilterEtapa = (etapa?: number) =>
    navigate({
      ...filter,
      etapa: etapa,
    });

  const handleFilterData = (data?: Date) => {
    navigate({
      ...filter,
      data: data ? set(data, { hours: 12, minutes: 0, seconds: 0 }) : undefined,
    });
  };

  const handleFilter = async () => {
    try {
      if (filter.idTurma) {
        setLoading(true);
        const where: Record<string, unknown> = {
          idCurso: filter.idCurso,
          idSerie: filter.idSerie,
          idTurma: filter.idTurma,
          questionario: {
            anoLetivo: filter.anoLetivo || escola.anoSite,
          },
        };

        if (filter.data) {
          where.data = format(filter.data, "yyyy-MM-dd");
        }

        if (filter.etapa) {
          where.bimestre = filter.etapa;
        }

        if (filter.idProfessor) {
          where.idProfessor = filter.idProfessor;
        }

        if (filter.idAluno) {
          where.idAluno = filter.idAluno;
        }

        if (filter.idQuestionario) {
          where.idQuestionario = filter.idQuestionario;
        }

        const params = {
          select: [
            ...Object.keys(yupQuestAvaliacao.fields),
            "createdAt",
            "aluno.uid",
            "aluno.nome",
            "aluno.nomeusual",
            "questionario.id",
            "questionario.nome",
            "professor.id",
            "professor.nome",
            "turma.tituloTurma",
            "turma.id",
          ],
          order: { createdAt: "DESC" },
          where,
          take: rowsPerPage,
          skip: rowsPerPage * page,
        };
        const { data } = await api.get<[IDMMAvaQuestionario[], number]>(
          "avaliacao-questionario",
          {
            params,
            cache: { ignoreCache: true },
          }
        );
        if (Array.isArray(data) && Array.isArray(data[0])) {
          setAvaliacoes(data);
        }
      } else {
        setAvaliacoes([[], 0]);
      }
    } catch (err) {
      snack.warning(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (reload?: boolean) => {
    if (reload) handleFilter();
    handleHide();
  };

  const handleCadastro = () => {
    if (!filter.idCurso || !filter.idSerie || !filter.idTurma) {
      return;
    }
    setAvaliacao({
      idCurso: filter.idCurso,
      idSerie: filter.idSerie,
      idTurma: filter.idTurma,
      bimestre: filter.etapa,
      idAluno: filter.idAluno ? [filter.idAluno] : [],
      idProfessor: filter.idProfessor,
      idQuestionario: filter.idQuestionario,
    });
    showCadastro();
  };

  const handleEdit = (item: IDMMAvaQuestionario) => {
    if (!item.idCurso || !item.idSerie || !item.idTurma) {
      return;
    }
    setAvaliacao({
      id: item.id,
      idAluno: [item.idAluno],
      bimestre: item.bimestre,
      data: item.data,
      idCurso: item.idCurso,
      idProfessor: item.idProfessor,
      idQuestionario: item.idQuestionario,
      idSerie: item.idSerie,
      idTurma: item.idTurma,
      nota: item.nota,
    });
    showCadastro();
  };

  const handleResponder = (item: IDMMAvaQuestionario) => {
    history.push(`/questionario/responder/${item.id}`);
  };

  const handleVisualizar = (item: IDMMAvaQuestionario) => {
    const url = `${Environment.RELATORIO_API_URL}/api/${usuario?.escolaId}/questionario/${item.id}/impressao/`;
    const papel =
      Object.keys(configPapelPadrao).length > 0
        ? configPapelPadrao
        : tamanhoPapeis.find((f) => f.name == "A4");
    const params: Record<string, string> = {
      authorization: `Bearer ${usuario?.token}`,
    };
    if (papel) {
      params.width = `${
        papel.height > papel.width ? papel.width : papel.height
      }mm`;
      params.height = `${
        papel.height > papel.width ? papel.height : papel.width
      }mm`;
      params.marginTop = `${papel.marginTop}mm`;
      params.marginBottom = `${papel.marginBottom}mm`;
      params.marginLeft = `${papel.marginLeft}mm`;
      params.marginRight = `${papel.marginBottom}mm`;
    }
    const query = new URLSearchParams(params);
    window.open(url.concat("?").concat(query.toString()), "_blank");
  };

  const getAlunos = async () => {
    if (filter.idTurma) {
      try {
        setLoadingMatricula(true);
        const { data } = await api.get<ITurma | undefined>(
          `turma/${filter.idTurma}`,
          {
            cache: {
              ignoreCache: true,
            },
            params: {
              select: [
                "id",
                "matriculas.id",
                "matriculas.nrChamada",
                "matriculas.situacao",
                "matriculas.aluno.nome",
                "matriculas.aluno.uid",
                "matriculas.aluno.codigo",
                "matriculas.aluno.nomeusual",
                "matriculas.aluno.imagemAluno.caminhoArquivo",
              ],
              order: {
                matriculas: { nrChamada: "ASC" },
              },
            },
          }
        );
        if (data && Array.isArray(data.matriculas)) {
          setMatriculas(data.matriculas);
        }
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoadingMatricula(false);
      }
    } else {
      setMatriculas([]);
    }
  };

  const getQuestionarios = async () => {
    try {
      setLoadingQuestionarios(true);
      const { data } = await api.get<[IQuestionario[], number]>(
        "questionario",
        {
          params: {
            where: { anoLetivo: filter.anoLetivo },
            select: [
              "id",
              "nome",
              "diario",
              "multiBimestre",
              "idSerie",
              "idCurso",
            ],
            order: { nome: "ASC" },
          },
        }
      );
      if (Array.isArray(data) && Array.isArray(data[0])) {
        setQuestionarios(data[0]);
      }
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setLoadingQuestionarios(false);
    }
  };

  const onChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(parseInt(value, 10));
  };

  const {
    Component,
    navigate,
    filter,
    selecionados: { curso, turma, serie },
  } = useFilter<IFilterAvaliacaoQuestionario>({
    pathname: "/questionario/avaliacoes/",
    queryKeys: [
      "idCurso",
      "idSerie",
      "idTurma",
      "etapa",
      "anoLetivo",
      "data",
      "idAluno",
      "idProfessor",
      "idQuestionario",
    ],
    initialValue: {
      anoLetivo: parseInt(escola.anoSite, 10),
    },
    options: {
      curso: { onClick: handleFilterCurso, mostrarTodos: true },
      serie: { onClick: handleFilterSerie, mostrarTodos: true },
      turma: { onClick: handleFilterTurma, mostrarTodos: true },
      etapa: { onClick: handleFilterEtapa, mostrarTodos: true },
      data: { onClick: handleFilterData, allowsToClean: true },
      anoLetivo: { onClick: handleFilterAnoLetivo },
    },
  });

  useEffect(() => {
    getAlunos();
  }, [filter.idTurma]);

  useEffect(() => {
    handleFilter();
  }, [
    filter.anoLetivo,
    filter.data,
    filter.idTurma,
    filter.etapa,
    filter.idAluno,
    filter.idProfessor,
    filter.idQuestionario,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    getQuestionarios();
  }, [filter.anoLetivo]);

  useEffect(() => {
    (async () => {
      if (tamanhoPapeis.length == 0) {
        dispatch(getPapeis());
      }
    })();
  }, []);

  const professores = utils.getProfessoresTreeView({
    curso,
    serie,
    turma,
    professores: profs,
  });

  return (
    <Main
      title="Avaliações de Questionarios"
      loading={loading || loadingMatricula || loadingQuestionarios}
      rightbarIcon={<FilterIcon />}
      rightbarChildren={
        <Filter
          Component={Component}
          filter={filter}
          navigate={navigate}
          matriculas={matriculas}
          professores={professores}
          questionarios={questionarios.filter(
            (f) =>
              f.idSerie == null || f.idSerie == serie?.id || serie == undefined
          )}
        />
      }
    >
      <DialogDelete />
      <DialogCadastro>
        {curso && avaliacao && (
          <CadastroAvaQuestionario
            matriculas={matriculas}
            questionarios={questionarios.filter(
              (f) =>
                f.idSerie == null ||
                f.idSerie == serie?.id ||
                serie == undefined
            )}
            curso={curso}
            handleClose={handleClose}
            item={avaliacao}
            professores={professores}
          />
        )}
      </DialogCadastro>
      {avaliacoes[0].length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <MUITableRow>
                {!isMd && <TableCell>Turma</TableCell>}
                <TableCell>Aluno</TableCell>
                <TableCell>Etapa</TableCell>
                {!isMd && !isXl && <TableCell>Questionário</TableCell>}
                {!isMd && <TableCell>Professor</TableCell>}
                <TableCell></TableCell>
              </MUITableRow>
            </TableHead>
            <TableBody>
              {avaliacoes[0].map((m, i) => (
                <TableRow
                  key={`ava-quest-${i}`}
                  avaliacao={m as IAvaQuestionario}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  handleResponder={handleResponder}
                  handleVisualizar={handleVisualizar}
                />
              ))}
            </TableBody>
          </Table>
          <Pagination
            rowsPerPageOptions={rowsPerPageOptions}
            count={avaliacoes[1]}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        </TableContainer>
      )}
      {(!filter.idCurso || !filter.idSerie || !filter.idTurma) && !loading && (
        <EmptyState
          type="search"
          title="Selecione no filtro"
          subtitle="Selecione ao menos uma turma para apresentar as avaliações"
        />
      )}
      {avaliacoes[0].length == 0 &&
        !loading &&
        filter.idCurso &&
        filter.idSerie &&
        filter.idTurma && (
          <EmptyState
            type="404"
            title="Nenhuma avaliação encontrada!"
            subtitle="O Filtro selecionado não encontrou nenhuma avaliação"
          />
        )}
      {filter.idCurso && filter.idSerie && filter.idTurma && (
        <Float onClick={handleCadastro} title="Adicionar avaliação" />
      )}
    </Main>
  );
};
